import { useEffect, useMemo, useState } from 'react';
import adminApi from '@/lib/adminApi';

const FALLBACK_AI_MENTOR_PLANS = [
  {
    id: 'starter',
    name: 'Starter Mentor',
    monthly_price: 99,
    feedback_quota: 5,
  },
  {
    id: 'creator',
    name: 'Creator Mentor',
    monthly_price: 199,
    feedback_quota: 10,
  },
  {
    id: 'professional',
    name: 'Professional Mentor',
    monthly_price: 399,
    feedback_quota: 20,
  },
  {
    id: 'studio',
    name: 'Studio Mentor',
    monthly_price: 499,
    feedback_quota: 50,
  },
  {
    id: 'payg',
    name: 'Pay-as-you-go Review',
    monthly_price: 25,
    feedback_quota: 1,
  },
];

const formatDate = (value) => {
  if (!value) return 'Not set';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const normalizeArtists = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.artists)) return payload.artists;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const normalizePlans = (plans) => {
  const source = Array.isArray(plans) && plans.length ? plans : FALLBACK_AI_MENTOR_PLANS;
  return source.filter((plan) => plan?.id && plan.id !== 'free');
};

const getArtistName = (artist) => artist?.name || artist?.artist_name || artist?.username || artist?.email || 'Unnamed artist';

const getArtistLabel = (artist) => {
  const name = getArtistName(artist);
  const city = artist?.city || artist?.location || 'No city';
  const email = artist?.email || 'No email';
  const state = artist?.is_verified || artist?.verified ? 'Verified' : 'Needs review';
  return `${name} - ${city} - ${email} - ${state}`;
};

const getArtistSearchText = (artist) => [
  getArtistName(artist),
  artist?.email,
  artist?.city,
  artist?.location,
  artist?.category,
  artist?.subcategory,
  artist?.is_verified || artist?.verified ? 'verified' : 'needs review unverified',
].filter(Boolean).join(' ').toLowerCase();

const getManualActivationError = (requestError) => {
  const detail = requestError.response?.data?.detail;
  if (requestError.response?.status === 404 || detail === 'Not Found') {
    return 'AI Mentor manual activation route is not live on the backend yet. Redeploy the backend, then try again.';
  }
  return detail || 'Unable to activate AI Mentor Pro manually.';
};

const AdminAiMentor = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [selectedArtistId, setSelectedArtistId] = useState('');
  const [manualPlanId, setManualPlanId] = useState('creator');
  const [manualDays, setManualDays] = useState(30);
  const [manualNote, setManualNote] = useState('');
  const [artistSearch, setArtistSearch] = useState('');
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualResult, setManualResult] = useState('');
  const [manualResultType, setManualResultType] = useState('');

  const loadMentor = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminApi.get('/admin/ai-mentor');
      const mentorData = response.data || {};
      let nextArtists = normalizeArtists(mentorData);
      if (nextArtists.length === 0) {
        try {
          const artistsResponse = await adminApi.get('/admin/artists');
          nextArtists = normalizeArtists(artistsResponse.data);
        } catch (artistRequestError) {
          console.warn('AI Mentor artist fallback failed', artistRequestError);
        }
      }
      setData({
        ...mentorData,
        plans: Array.isArray(mentorData.plans) && mentorData.plans.length ? mentorData.plans : FALLBACK_AI_MENTOR_PLANS,
        artists: nextArtists,
      });
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to load AI Mentor Pro admin data.');
      try {
        const artistsResponse = await adminApi.get('/admin/artists');
        setData({
          plans: FALLBACK_AI_MENTOR_PLANS,
          artists: normalizeArtists(artistsResponse.data),
          requests: [],
          subscriptions: [],
          summary: {},
        });
      } catch (artistRequestError) {
        console.warn('AI Mentor fallback load failed', artistRequestError);
        setData({
          plans: FALLBACK_AI_MENTOR_PLANS,
          artists: [],
          requests: [],
          subscriptions: [],
          summary: {},
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMentor();
  }, []);

  const plans = useMemo(() => normalizePlans(data?.plans), [data?.plans]);
  const artists = useMemo(() => normalizeArtists(data).filter((artist) => artist?.id), [data]);
  const filteredArtists = useMemo(() => {
    const query = artistSearch.trim().toLowerCase();
    if (!query) return artists;
    return artists.filter((artist) => getArtistSearchText(artist).includes(query));
  }, [artistSearch, artists]);
  const selectedArtist = useMemo(
    () => artists.find((artist) => artist.id === selectedArtistId),
    [artists, selectedArtistId],
  );
  const requests = data?.requests || [];
  const pendingRequests = requests.filter((item) => item.status === 'pending');
  const subscriptions = data?.subscriptions || [];
  const activeSubscriptions = subscriptions.filter((item) => item.status === 'active');

  const approveRequest = async (requestItem, planId = requestItem.plan_id || 'creator') => {
    setError('');
    setStatus('');
    try {
      await adminApi.post(`/admin/ai-mentor/access-requests/${requestItem.id}/approve`, {
        plan_id: planId,
        billing_cycle: requestItem.billing_cycle || 'monthly',
        duration_days: 30,
        admin_note: `Approved ${planId} from early access request.`,
      });
      setStatus(`${requestItem.artist_name || 'Artist'} AI Mentor Pro access activated.`);
      loadMentor();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to approve AI Mentor request.');
    }
  };

  const rejectRequest = async (requestItem) => {
    if (!window.confirm(`Reject AI Mentor request from ${requestItem.artist_name || 'this artist'}?`)) return;
    setError('');
    setStatus('');
    try {
      await adminApi.post(`/admin/ai-mentor/access-requests/${requestItem.id}/reject`, {
        plan_id: requestItem.plan_id || 'creator',
        admin_note: 'Rejected by admin.',
      });
      setStatus('AI Mentor access request rejected.');
      loadMentor();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to reject AI Mentor request.');
    }
  };

  const activateManual = async () => {
    setManualResult('');
    setManualResultType('');
    if (!selectedArtistId) {
      setManualResult('Select an artist before manual activation.');
      setManualResultType('error');
      return;
    }
    if (!manualPlanId) {
      setManualResult('Select a mentor plan before manual activation.');
      setManualResultType('error');
      return;
    }
    setError('');
    setStatus('');
    setManualSubmitting(true);
    try {
      await adminApi.post('/admin/ai-mentor/subscriptions/manual', {
        artist_id: selectedArtistId,
        plan_id: manualPlanId,
        billing_cycle: 'monthly',
        duration_days: Number(manualDays || 30),
        admin_note: manualNote || 'Manual AI Mentor Pro beta activation.',
      });
      const artistName = selectedArtist ? getArtistName(selectedArtist) : 'Artist';
      const planName = plans.find((plan) => plan.id === manualPlanId)?.name || manualPlanId;
      const successMessage = `${artistName} has been activated on ${planName} for ${Number(manualDays || 30)} day(s).`;
      setStatus(successMessage);
      setManualResult(successMessage);
      setManualResultType('success');
      setManualNote('');
      loadMentor();
    } catch (requestError) {
      const message = getManualActivationError(requestError);
      setError(message);
      setManualResult(message);
      setManualResultType('error');
    } finally {
      setManualSubmitting(false);
    }
  };

  const updateSubscription = async (subscription, nextStatus) => {
    setError('');
    setStatus('');
    try {
      await adminApi.patch(`/admin/ai-mentor/subscriptions/${subscription.id}`, {
        status: nextStatus,
        admin_note: `Marked ${nextStatus} by admin.`,
      });
      setStatus(`Subscription marked ${nextStatus}.`);
      loadMentor();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to update AI Mentor subscription.');
    }
  };

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-orange-100 bg-white p-6 shadow-sm">
        <p className="text-xs font-black uppercase tracking-[0.28em] text-[#FF6F00]">AI Mentor Pro</p>
        <h1 className="mt-2 text-3xl font-black text-gray-950">Paid mentor access control</h1>
        <p className="mt-2 max-w-4xl text-sm font-semibold leading-6 text-gray-600">
          Manage early paid AI Mentor Pro access before the payment gateway is connected. Artists request access on the website, and admin can approve, pause, or manually activate plans.
        </p>
        <div className="mt-4 rounded-2xl border border-amber-100 bg-amber-50 p-4 text-sm font-bold leading-6 text-amber-900">
          During beta, approving a request does not charge the artist. It activates the selected plan for the chosen number of days, and usage is limited by that plan's monthly feedback quota.
        </div>
      </section>

      {error && <div className="rounded-2xl border border-red-100 bg-red-50 p-4 text-sm font-bold text-red-700">{error}</div>}
      {status && <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-sm font-bold text-emerald-800">{status}</div>}

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-3xl border border-orange-100 bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-gray-500">Pending requests</p>
          <p className="mt-2 text-4xl font-black text-[#FF6F00]">{data?.summary?.pending_requests || 0}</p>
        </div>
        <div className="rounded-3xl border border-emerald-100 bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-gray-500">Active plans</p>
          <p className="mt-2 text-4xl font-black text-emerald-700">{data?.summary?.active_subscriptions || 0}</p>
        </div>
        <div className="rounded-3xl border border-purple-100 bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-gray-500">Available paid plans</p>
          <p className="mt-2 text-4xl font-black text-[#4A148C]">{plans.length}</p>
        </div>
      </div>

      <section className="rounded-3xl border border-orange-100 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.22em] text-[#FF6F00]">Requests</p>
            <h2 className="mt-1 text-2xl font-black text-gray-950">Artists waiting for AI Mentor Pro</h2>
          </div>
          <button type="button" onClick={loadMentor} className="rounded-full border border-orange-200 px-4 py-2 text-sm font-bold text-[#FF6F00]">
            Refresh
          </button>
        </div>
        {loading && <p className="mt-4 text-sm font-semibold text-gray-500">Loading AI Mentor requests...</p>}
        {!loading && pendingRequests.length === 0 && <p className="mt-4 rounded-2xl bg-slate-50 p-4 text-sm font-semibold text-gray-500">No pending AI Mentor Pro requests.</p>}
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          {pendingRequests.map((requestItem) => (
            <article key={requestItem.id} className="rounded-2xl border border-orange-100 bg-orange-50/40 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-black text-gray-950">{requestItem.artist_name || 'Artist'}</h3>
                  <p className="text-sm font-semibold text-gray-600">{requestItem.artist_email || 'No email'} | {formatDate(requestItem.created_at)}</p>
                  <p className="mt-2 text-sm font-bold text-[#4A148C]">Requested: {plans.find((plan) => plan.id === requestItem.plan_id)?.name || requestItem.plan_id}</p>
                  <p className="mt-1 text-xs font-bold text-gray-500">
                    Approval grants quota-limited manual access. Payment collection can be added later.
                  </p>
                  {requestItem.note && <p className="mt-2 text-sm leading-6 text-gray-700">{requestItem.note}</p>}
                </div>
                <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-black text-amber-800">Pending</span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button type="button" onClick={() => approveRequest(requestItem, requestItem.plan_id || 'creator')} className="rounded-full bg-[#FF6F00] px-4 py-2 text-sm font-bold text-white">
                  Approve
                </button>
                <button type="button" onClick={() => rejectRequest(requestItem)} className="rounded-full border border-red-200 px-4 py-2 text-sm font-bold text-red-700">
                  Reject
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="rounded-3xl border border-purple-100 bg-white p-6 shadow-sm">
        <p className="text-xs font-black uppercase tracking-[0.22em] text-[#4A148C]">Manual activation</p>
        <h2 className="mt-1 text-2xl font-black text-gray-950">Give AI Mentor Pro to an artist</h2>
        <p className="mt-2 text-sm font-semibold leading-6 text-gray-600">
          Use this for beta users, founders, paid offline customers, or artists you want to test premium mentor features.
        </p>
        <p className="mt-1 text-xs font-bold text-purple-700">
          The plan controls feedback limits: Starter 5/month, Creator 10/month, Professional 20/month, Studio 50/month, Pay-as-you-go 1 credit.
        </p>
        <div className="mt-4 grid gap-2 lg:grid-cols-[1.4fr_0.8fr_0.6fr]">
          <div>
            <input
              type="search"
              value={artistSearch}
              onChange={(event) => setArtistSearch(event.target.value)}
              className="w-full rounded-2xl border border-purple-100 bg-purple-50/60 px-4 py-3 font-semibold outline-none transition focus:border-[#4A148C] focus:bg-white"
              placeholder="Find artist by name, email, city, category, verified..."
            />
            <p className="mt-1 text-xs font-bold text-purple-700">
              Showing {filteredArtists.length} of {artists.length} artist(s). Search before selecting to avoid scrolling.
            </p>
          </div>
        </div>
        {artistSearch.trim() && (
          <div className="mt-3 rounded-2xl border border-purple-100 bg-purple-50/50 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#4A148C]">Search results</p>
              {selectedArtist && (
                <p className="text-xs font-bold text-emerald-700">Selected: {getArtistName(selectedArtist)}</p>
              )}
            </div>
            {filteredArtists.length === 0 ? (
              <p className="mt-2 rounded-xl bg-white p-3 text-sm font-semibold text-gray-500">
                No artist matched this search. Try name, city, email, category, verified, or unverified.
              </p>
            ) : (
              <>
                <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                  {filteredArtists.slice(0, 12).map((artist) => {
                    const active = selectedArtistId === artist.id;
                    return (
                      <button
                        key={artist.id || artist.email}
                        type="button"
                        onClick={() => setSelectedArtistId(artist.id)}
                        className={`rounded-2xl border p-3 text-left transition ${
                          active
                            ? 'border-[#4A148C] bg-white shadow-sm'
                            : 'border-purple-100 bg-white/80 hover:border-[#4A148C] hover:bg-white'
                        }`}
                      >
                        <span className="block text-sm font-black text-gray-950">{getArtistName(artist)}</span>
                        <span className="mt-1 block text-xs font-semibold text-gray-600">
                          {artist?.city || artist?.location || 'No city'} - {artist?.email || 'No email'}
                        </span>
                        <span className="mt-1 block text-xs font-bold text-[#4A148C]">
                          {artist?.category || artist?.subcategory || 'Artist'} - {artist?.is_verified || artist?.verified ? 'Verified' : 'Needs review'}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {filteredArtists.length > 12 && (
                  <p className="mt-2 text-xs font-bold text-purple-700">
                    Showing first 12 results. Keep typing to narrow the artist list.
                  </p>
                )}
              </>
            )}
          </div>
        )}
        <div className="mt-4 grid gap-3 lg:grid-cols-[1.4fr_0.8fr_0.6fr]">
          <select value={selectedArtistId} onChange={(event) => setSelectedArtistId(event.target.value)} className="rounded-2xl border border-slate-200 px-4 py-3 font-semibold">
            <option value="">Select artist</option>
            {artists.length === 0 && <option value="" disabled>No artists loaded yet - click Refresh</option>}
            {artists.length > 0 && filteredArtists.length === 0 && <option value="" disabled>No matching artist found</option>}
            {filteredArtists.map((artist) => (
              <option key={artist.id || artist.email} value={artist.id}>
                {getArtistLabel(artist)}
              </option>
            ))}
          </select>
          <select value={manualPlanId} onChange={(event) => setManualPlanId(event.target.value)} className="rounded-2xl border border-slate-200 px-4 py-3 font-semibold">
            {plans.length === 0 && <option value="" disabled>No paid plans loaded yet</option>}
            {plans.map((plan) => <option key={plan.id} value={plan.id}>{plan.name}</option>)}
          </select>
          <input type="number" min="1" max="730" value={manualDays} onChange={(event) => setManualDays(event.target.value)} className="rounded-2xl border border-slate-200 px-4 py-3 font-semibold" placeholder="Days" />
        </div>
        <textarea value={manualNote} onChange={(event) => setManualNote(event.target.value)} className="mt-3 min-h-[90px] w-full rounded-2xl border border-slate-200 px-4 py-3 font-semibold" placeholder="Admin note, e.g. Early beta access for 30 days" />
        {!loading && artists.length === 0 && (
          <p className="mt-3 rounded-2xl border border-amber-100 bg-amber-50 p-3 text-sm font-bold text-amber-900">
            Artists could not be loaded for manual activation. Use Refresh once the backend/admin artist API is available.
          </p>
        )}
        <button
          type="button"
          onClick={activateManual}
          disabled={manualSubmitting || !selectedArtistId || !manualPlanId}
          className={`mt-3 rounded-full px-6 py-3 text-sm font-black text-white ${
            manualSubmitting || !selectedArtistId || !manualPlanId
              ? 'cursor-not-allowed bg-slate-400'
              : 'bg-[#4A148C] hover:bg-[#3a0f70]'
          }`}
        >
          {manualSubmitting ? 'Activating...' : !selectedArtistId ? 'Select artist first' : 'Activate manually'}
        </button>
        {manualResult && (
          <p className={`mt-3 rounded-2xl border p-3 text-sm font-bold ${
            manualResultType === 'error'
              ? 'border-red-100 bg-red-50 text-red-700'
              : 'border-emerald-100 bg-emerald-50 text-emerald-800'
          }`}>
            {manualResult}
          </p>
        )}
      </section>

      <section className="rounded-3xl border border-emerald-100 bg-white p-6 shadow-sm">
        <p className="text-xs font-black uppercase tracking-[0.22em] text-emerald-700">Active and recent</p>
        <h2 className="mt-1 text-2xl font-black text-gray-950">AI Mentor Pro subscriptions</h2>
        <div className="mt-4 space-y-3">
          {activeSubscriptions.length === 0 && <p className="rounded-2xl bg-slate-50 p-4 text-sm font-semibold text-gray-500">No active AI Mentor Pro plans yet.</p>}
          {subscriptions.slice(0, 80).map((subscription) => (
            <article key={subscription.id} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-black text-gray-950">{subscription.artist_name || subscription.artist_id}</h3>
                  <p className="text-sm font-semibold text-gray-600">
                    {subscription.plan?.name || subscription.plan_id} | {subscription.status} | Ends {formatDate(subscription.current_period_end)}
                  </p>
                  <p className="mt-1 text-xs font-bold text-gray-500">
                    Feedback used this month: {subscription.usage?.feedback_used || 0} / {subscription.plan?.feedback_quota || 0}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {subscription.status !== 'active' && (
                    <button type="button" onClick={() => updateSubscription(subscription, 'active')} className="rounded-full bg-emerald-600 px-4 py-2 text-xs font-bold text-white">
                      Reactivate
                    </button>
                  )}
                  {subscription.status === 'active' && (
                    <button type="button" onClick={() => updateSubscription(subscription, 'paused')} className="rounded-full border border-amber-200 px-4 py-2 text-xs font-bold text-amber-700">
                      Pause
                    </button>
                  )}
                  <button type="button" onClick={() => updateSubscription(subscription, 'cancelled')} className="rounded-full border border-red-200 px-4 py-2 text-xs font-bold text-red-700">
                    Cancel
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AdminAiMentor;
